import React, {useEffect, useRef, useState} from "react";
import {useDispatch, useSelector} from "react-redux";
import {PaymentType, ServiceType, OrderStatus, PaymentStatus} from "../../enums/enums";
import {camelToTitle, removeEmptyStrings} from "../Utility";
import Card from "../ui/card/Card";
import {updateItems} from "../../store/cart-slice";
import moment from "moment-timezone";
import {fetchCustomers} from "../../store/customer-slice";

const phoneRegex = /^(?:\+91[-\s]?|91[-\s]?|0)?[1-9]\d{9}$/;
const timezone = 'Asia/Kolkata';
const Cart = ({onSubmit, isEditable = false, editableItem = {}, onNumberSelect}) => {
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [filteredSuggestions, setFilteredSuggestions] = useState([]);
    const userRole = useSelector((state) => state.auth.selectedRole);
    const dispatch = useDispatch();
    const {
        createStatus,
        customer,
        updateStatus,
        fetchCustomerStatus,
        error
    } = useSelector(state => state.order);

    const {
        items: cartItems,
        total: cartTotal,
    } = useSelector((state) => state.cart)


    const [errors, setErrors] = useState({
        phone: "",
        items: "",
    });

    const [inputValues, setInputValues] = useState({
        serviceType: "DineIn",
        paymentType: "Online",
        paymentStatus: 1,
        orderStatus: 3,
        fName: "",
        lName: "",
        orderTime: "",
        officeNo: "",
        floor: "",
        phone: "",
        tableNo: ""
    });

    useEffect(() => {
        if (isEditable) {
            dispatch(updateItems({items: editableItem.items, total: editableItem.totalPrice}));
            setInputValues((prev) => {
                return {
                    ...prev,
                    phone: editableItem?.user?.phone,
                    serviceType: editableItem.serviceType,
                    paymentType: editableItem.paymentType,
                    paymentStatus: editableItem.paymentStatus,
                    orderStatus: editableItem.orderStatus,
                    orderTime: editableItem.orderTime ? moment(editableItem.orderTime).tz(timezone).format('YYYY-MM-DD') : '',
                    tableNo: editableItem?.tableNo ? String(editableItem.tableNo) : "",
                };
            });
        }
    }, [editableItem, dispatch, isEditable]);


    const customerInfo = {
        name: `${customer?.fName || ''} ${customer?.lName || ''}`,
        phone: customer?.phone || '',
        ...(customer?.officeNo && {officeNo: customer.officeNo}),
        ...(customer?.floor && {floor: customer.floor}),
    };

    // Handle input changes
    const handleChange = (e) => {
        const {name, value} = e.target;
        setInputValues(prev => ({
            ...prev,
            [name]: String(value)
        }));

        setErrors(prev => ({
            ...prev,
            [name]: "",
        }));
    };

    const handleDateChange = (e) => {
        const {name, value} = e.target;

        if (name === "orderTime") {
            if (!value) {
                // If the date input is cleared
                setInputValues((prev) => ({
                    ...prev,
                    orderTime: undefined,
                }));
                return;
            }

            setInputValues((prev) => ({
                ...prev,
                [name]: value,
            }));
        }
    };

    // handle number change
    const handlePhoneBlur = (e) => {
        const phone = e.target.value.trim();
        if (phone && !phoneRegex.test(phone)) {
            setErrors({phone: "Please enter a valid Indian phone number (e.g. 9876543210, +919876543210, 01722001234)"});
            return;
        } else {
            setErrors((prev) => ({...prev, phone: ""}));
        }
        if (phone !== "") {
            onNumberSelect(phone);
        }
    };

    // Handle Key Down
    const handleKeyDown = (e) => {
        if (e.key === "Enter") {
            e.preventDefault(); // Stop form submission
        }
    };

    // Handle form submission
    const handleSubmit = (e) => {
        e.preventDefault();

        let newErrors = {};

        const phone = inputValues?.phone?.trim() || '';
        if (phone && !phoneRegex.test(phone)) {
            newErrors.phone = "Enter a valid 10-digit phone number";
        }
        if (cartItems.length === 0) {
            newErrors.items = "Please add at least one item to the cart";
        }
        if (inputValues.paymentType === PaymentType.Credit && !inputValues.phone?.trim()) {
            newErrors.phone = "Phone no. is required";
        }

        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            return;
        }
        // Prepare cart items for submission
        const transformedItems = cartItems.map(({productId, quantity}) => ({
            product: productId,
            quantity,
        }));

        const baseData = {
            serviceType: inputValues?.serviceType,
            tableNo: +inputValues?.tableNo,
            orderTime: inputValues?.orderTime,
            paymentType: inputValues?.paymentType,
            paymentStatus: +inputValues?.paymentStatus,
            orderStatus: +inputValues.orderStatus,
        };

        const customerData =
            fetchCustomerStatus === "failed"
                ? {
                    fName: inputValues?.fName,
                    lName: inputValues?.lName,
                    phone: inputValues?.phone,
                    customer: {
                        address: {
                            officeNo: inputValues?.officeNo,
                            floor: inputValues?.floor,
                        },
                    },
                }
                : {
                    user: customer?._id || "",
                    phone: inputValues?.phone || ""
                };

        // Merge and clean the final data
        const submissionData = {
            ...removeEmptyStrings({...baseData, ...customerData}),
            items: transformedItems,
        };
        onSubmit(submissionData);
    };

    //To ensure that if the phone number is invalid or missing and Credit is somehow selected
    const checkPaymentMethod = (e) => {
        const phone = e.target.value.trim();
        const isPhoneInvalid = !phoneRegex.test(phone);

        if (!isPhoneInvalid) return; // only act when phone is invalid

        // Define reset rules in one place
        const resetRules = [
            {
                field: "paymentType",
                invalidValue: PaymentType.Credit
            },
            {
                field: "paymentStatus",
                invalidValue: String(PaymentStatus.Pending) // change to Pending if you want Pending reset
            }
        ];

        setInputValues((prev) => {
            const updated = {...prev};

            resetRules.forEach(({field, invalidValue}) => {
                if (prev[field] === invalidValue) {
                    updated[field] = ""; // reset to default
                }
            });

            return updated;
        });
    };

    // If the payment type is Credit, set the payment status to Pending
    useEffect(() => {
        if (inputValues.paymentType === PaymentType.Credit) {
            setInputValues(prev => ({
                ...prev,
                paymentStatus: PaymentStatus.Pending
            }));
        }
    }, [inputValues.paymentType]);

    const cartRef = useRef(null);

    useEffect(() => {
        const cartEl = cartRef.current;
        if (!cartEl) return;

        const parentEl = cartEl.closest(".layout-content");
        if (!parentEl) return;

        const parentStyles = getComputedStyle(parentEl);
        const parentPaddingTop = parseFloat(parentStyles.paddingTop) || 0;
        const parentOffsetTop = parentEl.offsetTop || 0;
        const cartOffsetTop = cartEl.offsetTop || 0;

        cartEl.style.top = `${cartOffsetTop - (parentOffsetTop + parentPaddingTop)}px`;
        cartEl.style.height = `calc(100vh - ${1 + cartOffsetTop + parentPaddingTop}px)`;
        dispatch(fetchCustomers(userRole))
    }, [dispatch, userRole]);

    const {
        items: suggestions,
        fetchStatus: fetchCustomersStatus,
    } = useSelector(state => state.customer);


    return (
        <Card className="cart border-0 bg-primary bg-opacity-10 p-3 position-sticky slim-scrollbar" ref={cartRef}>
            <form className="cart-form" onSubmit={handleSubmit} style={{"--bs-border-color": "#aaa"}}>
                {(createStatus === "failed" || updateStatus === "failed") &&
                    <h6 className="text-danger fs-md">{error} </h6>}
                {errors.items && (
                    <div className="invalid-feedback d-block">
                        Please select items to order
                    </div>
                )}
                <div className="row">
                    {(!isEditable) && <div className="col-xl-6">
                        <div className="mb-2 position-relative">
                            <label className="form-label mb-1">Enter Mobile</label>
                            <input
                                className={`form-control form-control-sm ${errors.phone ? 'is-invalid' : ''}`}
                                name="phone"
                                type="text"
                                value={inputValues.phone}
                                onChange={(e) => {
                                    const value = e.target.value;
                                    setInputValues({...inputValues, phone: value});

                                    // Show all if input is empty
                                    const filtered = value.trim() === ''
                                        ? suggestions
                                        : suggestions.filter(s =>
                                            (`${s.fName ?? ''} - ${s.phone}${s.officeNo ? ` - ${s.officeNo}` : ''}`)
                                                .toLowerCase()
                                                .includes(value.toLowerCase())
                                        );

                                    setFilteredSuggestions(filtered);
                                    setShowSuggestions(true);
                                }}
                                onBlur={(e) => {
                                    setTimeout(() => setShowSuggestions(false), 100); // delay to allow click
                                    handlePhoneBlur(e);
                                    checkPaymentMethod(e);
                                }}
                                onFocus={() => {
                                    // Show all suggestions when input is focused
                                    setFilteredSuggestions(suggestions);
                                    setShowSuggestions(true);
                                }}
                                autoComplete="off"
                            />

                            {/* Suggestion Dropdown */}
                            {fetchCustomersStatus && showSuggestions && filteredSuggestions.length > 0 && (
                                <ul className="custom-menu position-absolute w-100 z-3 bg-white shadow-lg"
                                    style={{maxHeight: "200px", overflowY: "auto", top: '62px'}}>
                                    {filteredSuggestions.map((item, index) => {
                                        const label = `${item.fName ? item.fName + '-' : ''}  ${item.phone}${item.officeNo ? ` - ${item.officeNo}` : ''}`;
                                        return (
                                            <li
                                                key={index}
                                                className="list fs-sm px-2"
                                                onMouseDown={() => {
                                                    setInputValues({...inputValues, phone: item.phone});
                                                    setShowSuggestions(false);
                                                }}
                                            >
                                                {label}
                                            </li>
                                        );
                                    })}
                                </ul>
                            )}
                            {errors.phone && (
                                <div className="invalid-feedback d-block">
                                    {errors.phone}
                                </div>
                            )}
                        </div>

                    </div>}
                    {
                        fetchCustomerStatus === "failed" && !isEditable &&
                        <>
                            <div className="col-xl-6">
                                <div className="mb-2">
                                    <label className="form-label mb-1">First Name</label>
                                    <input
                                        className="form-control form-control-sm"
                                        name="fName"
                                        type="text"
                                        placeholder=""
                                        value={inputValues.fName}
                                        onChange={handleChange}
                                        onKeyDown={handleKeyDown}
                                    />
                                </div>
                            </div>
                            <div className="col-xl-6">
                                <div className="mb-2">
                                    <label className="form-label mb-1">Last Name</label>
                                    <input
                                        className="form-control form-control-sm"
                                        name="lName"
                                        type="text"
                                        placeholder="Last Name"
                                        value={inputValues.lName}
                                        onChange={handleChange}
                                        onKeyDown={handleKeyDown}
                                    />
                                </div>
                            </div>

                            <div className="col-xl-6">
                                <div className="mb-2">
                                    <label className="form-label mb-1">Office No</label>
                                    <input
                                        className="form-control form-control-sm"
                                        name="officeNo"
                                        type="text"
                                        placeholder="Office No (optional)"
                                        value={inputValues.officeNo}
                                        onChange={handleChange}
                                        onKeyDown={handleKeyDown}
                                    />
                                </div>
                            </div>

                            <div className="col-xl-6">
                                <div className="mb-2">
                                    <label className="form-label mb-1">Floor</label>
                                    <input
                                        className="form-control form-control-sm"
                                        name="floor"
                                        type="text"
                                        placeholder="Floor (optional)"
                                        value={inputValues.floor}
                                        onChange={handleChange}
                                        onKeyDown={handleKeyDown}
                                    />
                                </div>
                            </div>
                        </>
                    }
                    {
                        fetchCustomerStatus === "success" &&
                        <>
                            {
                                Object.keys(customerInfo).map((key) => (
                                    <div className="d-flex justify-content-between w-100 mb-1 fs-md" key={key}>
                                        <span className="text-opacity-75 text-dark text-capitalize">{key}</span>
                                        <span>{customerInfo[key]}</span>
                                    </div>
                                ))
                            }
                            <div className="px-3">
                                <hr style={{"--bs-border-width": "1px"}}/>
                            </div>
                        </>
                    }
                </div>

                <div className="row">
                    <div className="col-xl-6">
                        <div className="mb-3">
                            <label className="form-label mb-1">Service type</label>
                            <select
                                className={`form-select form-select-sm`}
                                name="serviceType"
                                value={inputValues.serviceType}
                                onChange={handleChange}
                            >
                                <option value="" disabled>--Select service type</option>
                                {
                                    Object.keys(ServiceType).map((key) => {
                                        const value = ServiceType[key]
                                        return <option key={key} value={value}>{camelToTitle(key)}</option>
                                    })
                                }
                            </select>
                        </div>
                    </div>
                    {(inputValues.serviceType === "DineIn") && <div className="col-xl-6">
                        <div className="mb-3">
                            <label className="form-label mb-1">Enter Table</label>
                            <input
                                className={`form-control form-control-sm`}
                                name="tableNo"
                                type="text"
                                value={inputValues.tableNo}
                                onChange={handleChange}
                                onKeyDown={handleKeyDown}
                            />
                        </div>
                    </div>}
                    <div className="col-xl-6">
                        <div className="mb-3">
                            <label className="form-label mb-1">Payment type</label>
                            <select
                                className="form-select form-select-sm"
                                name="paymentType"
                                value={inputValues.paymentType}
                                onChange={handleChange}
                            >
                                <option value="" disabled>--Select payment type</option>
                                {
                                    Object.keys(PaymentType).map((key) => {
                                        const value = PaymentType[key];
                                        const phone = inputValues.phone?.trim() || "";
                                        const isPhoneInvalid = !phoneRegex.test(phone);

                                        // Disable 'Credit' if phone is invalid or empty
                                        const isCredit = value === PaymentType.Credit;
                                        const shouldDisable = isCredit && isPhoneInvalid;

                                        return (
                                            <option key={key} value={value} disabled={shouldDisable}>
                                                {camelToTitle(key)}
                                            </option>
                                        );
                                    })
                                }
                            </select>
                        </div>
                    </div>
                    <div className="col-xl-6">
                        <div className="mb-3">
                            <label className="form-label mb-1">Order Date</label>
                            <input
                                className={`form-control form-control-sm`}
                                name="orderTime"
                                type="date"
                                value={inputValues.orderTime ? moment(inputValues.orderTime).tz(timezone).format('YYYY-MM-DD') : ''}
                                onChange={handleDateChange}
                                onKeyDown={handleKeyDown}/>
                        </div>
                    </div>
                    <div className="px-3">
                        <hr style={{"--bs-border-width": "1px"}}/>
                    </div>
                </div>

                <table className="table table-cart mb-0">
                    {cartItems.length > 0 &&
                        <>
                            <thead>
                            <tr>
                                <td className="text-opacity-50 text-dark fw-semibold" colSpan="2">Cart items</td>
                            </tr>
                            </thead>
                            <tbody>
                            {cartItems.map((item) => (
                                <tr key={item._id}>
                                    <td>
                                        {item.name || item.productName} (x{item.quantity})
                                    </td>
                                    <td className="text-end">
                                        &#8377;{item.sellPrice * item.quantity}
                                    </td>
                                </tr>
                            ))}
                            <tr>
                                <td className="p-0" colSpan={2}>
                                    <hr style={{"--bs-border-width": "1px"}}/>
                                </td>
                            </tr>
                            </tbody>
                        </>
                    }
                    <tbody>
                    <tr>
                        <td className="fw-semibold" colSpan="2">
                            Bill Details
                        </td>
                    </tr>
                    <tr>
                        <td className="text-opacity-75 text-dark">
                            Item Total
                        </td>
                        <td className="text-end">
                            &#8377;{cartTotal}
                        </td>
                    </tr>
                    <tr>
                        <td className="text-opacity-75 text-dark">
                            Platform free
                        </td>
                        <td className="text-end text-opacity-75 text-dark">
                            &#8377;0
                        </td>
                    </tr>
                    <tr>
                        <td className="text-opacity-75 text-dark">
                            Delivery Free
                        </td>
                        <td className="text-opacity-75 text-dark text-end">
                            &#8377;0
                        </td>
                    </tr>
                    <tr>
                        <td className="text-opacity-75 text-dark">
                            Discount Applied
                        </td>
                        <td className="text-opacity-75 text-dark text-end">
                            -&#8377;0
                        </td>
                    </tr>
                    <tr>
                        <td className="p-0" colSpan={2}>
                            <hr style={{"--bs-border-width": "1px"}}/>
                        </td>
                    </tr>
                    <tr className="fw-semibold">
                        <td>
                            Bill total
                        </td>
                        <td className="text-end">
                            &#8377;{cartTotal + 9 + 9 - 18}
                        </td>
                    </tr>
                    <tr>
                        <td className="p-0" colSpan={2}>
                            <hr style={{"--bs-border-width": "1px"}}/>
                        </td>
                    </tr>
                    </tbody>
                </table>
                <div className="row">
                    <div className="col-xl-6">
                        <div className="mb-3">
                            <label className="form-label mb-1">Payment status</label>
                            <select
                                className="form-select form-select-sm"
                                name="paymentStatus"
                                value={inputValues.paymentStatus}
                                onChange={handleChange}
                            >
                                <option value="" disabled>--Select payment status</option>
                                {
                                    Object.keys(PaymentStatus).map((key) => {
                                        const value = PaymentStatus[key];
                                        const phone = inputValues.phone?.trim() || "";
                                        const isPhoneInvalid = !phoneRegex.test(phone);

                                        // Disable 'Pending' if phone is invalid or empty
                                        const isPending = value === PaymentStatus.Pending;
                                        const shouldDisable = isPending && isPhoneInvalid;

                                        return (
                                            <option key={key} value={value} disabled={shouldDisable}>
                                                {camelToTitle(key)}
                                            </option>
                                        );
                                    })
                                }
                            </select>
                        </div>
                    </div>
                    <div className="col-xl-6">
                        <div className="mb-3">
                            <label className="form-label mb-1">Order status</label>
                            <select
                                className={`form-select form-select-sm`}
                                name="orderStatus"
                                value={inputValues.orderStatus}
                                onChange={handleChange}
                            >
                                <option value="" disabled>--Select order status</option>
                                {
                                    Object.keys(OrderStatus).map((key) => {
                                        const value = OrderStatus[key];
                                        return <option key={key} value={value}>{camelToTitle(key)}</option>
                                    })
                                }
                            </select>
                        </div>
                    </div>
                    <div className="col-12">
                        <button type="submit" className="btn bg-primary bg-opacity-25 fw-medium rounded-2">
                            {isEditable ? "Update order" : "Submit order"}
                        </button>
                    </div>
                </div>
            </form>
        </Card>
    );
}

export default Cart;
